/**
 * Phase 6 cross-cutting PR 3 + PR 1c-c — `<BugReportTrigger>` · two render variants.
 *
 * **`'sidenav-row'`** (default · used by Default+ SideNav footer)
 * Labeled utility row matching the mockup's `.util` pattern:
 *
 *   🐞  Report a bug
 *
 * **`'icon-button'`** (PR 1c-c · used by Mobile TopBar)
 * Compact 32px icon-only button so mobile users can file a bug
 * report from any of the four mobile routes (today/settings/team/
 * races). Sits next to the BellTrigger icon-button in TopBar.
 *
 * Both variants share the same modal (`<BugReportModal>`) and the
 * locked 5-key privacy redaction shipped in PR 3.
 *
 * Renders nothing when the user is signed out (the modal needs auth
 * to submit anyway; vanilla `updateBugFabVisibility` also hides
 * when no userId).
 */

import { useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { BugReportModal } from '../BugReportModal/BugReportModal';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './BugReportTrigger.module.css';

export type BugReportTriggerVariant = 'sidenav-row' | 'icon-button';

interface BugReportTriggerProps {
  /**
   * Render shape. `'sidenav-row'` (default) is the labeled utility
   * row for SideNav footer. `'icon-button'` is the compact 32px
   * icon used by Mobile's TopBar slot, matching BellTrigger's
   * icon-button variant.
   */
  variant?: BugReportTriggerVariant;
}

export function BugReportTrigger({
  variant = 'sidenav-row',
}: BugReportTriggerProps = {}): JSX.Element | null {
  const { user } = useAuthContext();
  const [open, setOpen] = useState(false);
  const iconsOn = useIconSystem();

  if (!user) return null;

  const isIconButton = variant === 'icon-button';

  return (
    <>
      <button
        type="button"
        className={isIconButton ? styles.iconButton : styles.row}
        aria-label="Report a bug"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        {/* A2 — `sys.bug`. A FOUNDER SIGHTING (audit N-18): this 🐞 renders in
            the SideNav footer row AND the mobile TopBar button, on every
            signed-in surface, so it was one of the two most-seen emoji left.
            Sized to each variant's own box, same as the bell beside it. */}
        <span
          className={isIconButton ? styles.iconGlyph : styles.glyph}
          aria-hidden="true"
        >
          {iconsOn ? <Icon name="sys.bug" size={isIconButton ? 20 : 16} /> : '🐞'}
        </span>
        {!isIconButton ? <span className={styles.label}>Report a bug</span> : null}
      </button>
      <BugReportModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
